/* eslint-disable @typescript-eslint/no-unused-vars */
import { CacheType } from '@descomplica/caching-browser';
import {
  BaseHttpGateway,
  CollaborationResult,
  RecognitionManager,
} from '@descomplica/microfrontend-core';
import axios from 'axios';
import { injectable } from 'tsyringe';

import { ClassroomDto } from '@/application/interfaces/classroom/dtos/get-classroom-by-topic';
import {
  CertificateEnabledDto,
  CoursesForClassroomDto,
  GetStreamingByGuidInput,
  getStreamingByGuidResponse,
  IPegasusGateway,
  StudyPlanDto,
  UserDto,
} from '@/application/interfaces/classroom/pegasus.gateway';
import { PegasusGatewayResponse } from '@/application/interfaces/classroom/pegasus-gateway-response';
import { InfrastructureError } from '@/application/interfaces/infrastructure-error';
import envs from '@/infrastructure/config/envs';

@injectable()
export class PegasusGateway extends BaseHttpGateway implements IPegasusGateway {
  constructor(private readonly recognitionManager: RecognitionManager) {
    super();
  }

  async getIdentifiedUser(): Promise<CollaborationResult<UserDto>> {
    const { data } = await this.axios.get(`/identified-user`);
    return data;
  }

  async getClassroomByCourseGuid(guid: string): Promise<PegasusGatewayResponse<ClassroomDto>> {
    const { data } = await this.axios.get(`/classroom/${guid}`);

    return {
      data: data.data,
      error: undefined,
    };
  }

  async recordLessonProgress(
    guidCourse: string,
    guidVideo: string,
    usage: number,
    student: string,
  ): Promise<PegasusGatewayResponse> {
    try {
      const url = `/progress/video/${guidVideo}`;
      const result = (
        await this.axios.post(url, {
          courseGuid: guidCourse,
          student_guid: student,
          video_guid: guidVideo,
          usage,
        })
      ).data;

      return { data: result, error: undefined, identifier: result.identifier };
    } catch (error: unknown) {
      this.handleError(error);
    }
  }

  async recordVideoProgress(
    guidVideo: string,
    usage: number,
    student: string,
  ): Promise<PegasusGatewayResponse> {
    try {
      const url = `/progress/video`;
      const result = (
        await this.axios.post(url, {
          student_guid: student,
          video_guid: guidVideo,
          usage,
        })
      ).data;

      return { data: result, error: undefined, identifier: result.identifier };
    } catch (error: unknown) {
      this.handleError(error);
    }
  }

  async recordCourseProgress(courseGuid, studentGuid): Promise<PegasusGatewayResponse> {
    try {
      const url = `/progress/course/${courseGuid}`;
      const result = (
        await this.axios.post(url, {
          student_guid: studentGuid,
        })
      ).data;

      return { data: result, error: undefined, identifier: result.identifier };
    } catch (error: unknown) {
      this.handleError(error);
    }
  }

  async recordVideoWatching(
    courseGuid: string,
    lessonGuid: string,
    videoGuid: string,
    watchingLocation: string,
  ): Promise<PegasusGatewayResponse> {
    return {
      data: {
        message: '[RecordVideoWatchingCollaboration] - Video watching has been recorded',
        identifier: 'VideoWatchingRecorded',
      },
      error: undefined,
    };
  }

  async GetCoursesForClassroom(): Promise<CollaborationResult<CoursesForClassroomDto[]>> {
    try {
      const { data } = await this.axios.get('/courses-for-classroom');

      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async GetCoursesForClassroomByClassifier(
    classifierGuid: string,
  ): Promise<CollaborationResult<CoursesForClassroomDto[]>> {
    try {
      const { data } = await this.axios.get(`/courses-for-classroom/${classifierGuid}`);

      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getStudyPlan(guid: string): Promise<CollaborationResult<StudyPlanDto>> {
    const { data } = await this.axios.get(`/study-plan/${guid}`);
    return data;
  }

  async getCertificateEnabled(guid: string): Promise<CollaborationResult<CertificateEnabledDto>> {
    const { data } = await this.axios.get(`/certificate/course/${guid}`);
    return data;
  }

  async getStreamingByGuid({
    courseGuid,
    streamingGuid,
  }: GetStreamingByGuidInput): Promise<CollaborationResult<getStreamingByGuidResponse>> {
    const { data } = await this.axios.get(`/courses/${courseGuid}/live/${streamingGuid}`);

    return data;
  }

  private handleError(error: unknown) {
    console.error('PegasusGateway');
    if (axios.isAxiosError(error)) {
      console.error('axios.isAxiosError', error);
      const data = error?.response?.data;
      if (!data?.identifier) {
        throw new InfrastructureError(data);
      }

      throw error;
    }

    console.error('Unknown Error:', error);
    throw new InfrastructureError(error);
  }

  protected getEndpoint(): string {
    return `${envs.pegasusUrl}/application-management`;
  }

  protected getCacheNamespace(): string {
    return 'classroom-pegasus-gateway';
  }

  protected getCacheType(): CacheType {
    return CacheType.LOCALSTORAGE;
  }

  protected getBearerToken(): string {
    return this.recognitionManager.getAccessToken();
  }

  protected getHeaders(): object {
    // no cache for now
    return {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    };
  }
}
